import { feedItems, hotList } from "@/mocks/homeData";

const trendIcon: Record<string, string> = {
  up: "ri-arrow-up-line text-accent-600",
  down: "ri-arrow-down-line text-secondary-600",
  flat: "ri-subtract-line text-foreground-400",
};

export default function LiveFeedSection() {
  return (
    <section id="feed" className="w-full px-4 py-10 md:px-8 md:py-12">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_340px]">
        <div>
          <div className="mb-4 flex items-end justify-between gap-4 border-b border-background-200 pb-3">
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
                <i className="ri-live-line text-lg" style={{ lineHeight: 1 }} />
              </span>
              <div className="flex flex-col leading-tight">
                <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                  今日瓜料 · 实时滚更
                </h2>
                <span className="text-xs text-foreground-500">短讯并行，信息密度优先，按更新时间倒序</span>
              </div>
            </div>
            <a
              href="#topics"
              className="hidden shrink-0 items-center gap-1 whitespace-nowrap text-sm text-foreground-600 transition-colors hover:text-primary-700 sm:flex"
            >
              按话题浏览
              <i className="ri-arrow-right-s-line h-4 w-4" style={{ lineHeight: 1 }} />
            </a>
          </div>

          <div className="flex flex-col divide-y divide-background-200 overflow-hidden rounded-lg border border-background-200 bg-background-50">
            {feedItems.map((item, index) => (
              <article
                key={item.id}
                className="reveal group flex cursor-pointer items-start gap-4 p-4 transition-colors hover:bg-background-100"
                style={{ animationDelay: `${index * 60}ms` }}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    <span className="rounded-sm bg-secondary-100 px-2 py-0.5 font-medium text-secondary-800">
                      {item.tag}
                    </span>
                    <span className="font-label text-foreground-400">{item.time}</span>
                    {(item.heat > 8000) && (
                      <span className="rounded-sm bg-accent-100 px-2 py-0.5 font-medium text-accent-800">
                        高热度
                      </span>
                    )}
                  </div>

                  <h3 className="mt-2 text-sm font-bold leading-relaxed text-foreground-950 transition-colors group-hover:text-primary-700 md:text-base">
                    {item.title}
                  </h3>
                  <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-foreground-600 md:text-sm">
                    {item.summary}
                  </p>

                  <div className="mt-2 flex items-center gap-4 text-xs text-foreground-500">
                    <span className="flex items-center gap-1">
                      <i className="ri-fire-line h-3.5 w-3.5 text-accent-500" style={{ lineHeight: 1 }} />
                      {item.heat.toLocaleString()}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-chat-3-line h-3.5 w-3.5" style={{ lineHeight: 1 }} />
                      {item.comments} 条讨论
                    </span>
                  </div>
                </div>

                <div className="hidden h-20 w-20 shrink-0 overflow-hidden rounded-md border border-background-200 sm:block md:h-24 md:w-24">
                  <img
                    src={item.image}
                    alt={`${item.title} 配图`}
                    title={`黑料大瓜 ${item.tag} 细节分析`}
                    className="h-full w-full object-top"
                  />
                </div>
              </article>
            ))}
          </div>

          <button
            type="button"
            className="mt-4 w-full cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 py-3 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
          >
            加载更早的瓜料
          </button>
        </div>

        <aside className="lg:sticky lg:top-24 lg:self-start">
          <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
            <div className="flex items-center gap-2 border-b border-background-200 bg-primary-50 px-4 py-3">
              <i className="ri-bar-chart-2-line h-5 w-5 text-primary-600" style={{ lineHeight: 1 }} />
              <h2 className="font-heading text-base font-bold text-foreground-950">热瓜榜 · 每小时刷新</h2>
            </div>

            <ol className="divide-y divide-background-200">
              {hotList.map((item) => (
                <li key={item.rank}>
                  <a
                    href="#feed"
                    className="group flex cursor-pointer items-center gap-3 px-4 py-3 transition-colors hover:bg-background-100"
                  >
                    <span
                      className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-sm font-label text-xs font-bold ${
                        item.rank <= 3
                          ? "bg-accent-500 text-background-50"
                          : "bg-background-200 text-foreground-600"
                      }`}
                    >
                      {item.rank}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-sm text-foreground-800 transition-colors group-hover:text-primary-700">
                      {item.title}
                    </span>
                    <span className="flex shrink-0 items-center gap-1">
                      <span className="font-label text-xs text-foreground-400">{item.heat}</span>
                      <i className={`${trendIcon[item.trend]} h-4 w-4`} style={{ lineHeight: 1 }} />
                    </span>
                  </a>
                </li>
              ))}
            </ol>

            <a
              href="#topics"
              className="block cursor-pointer border-t border-background-200 px-4 py-3 text-center text-sm text-primary-700 transition-colors hover:bg-primary-50"
            >
              查看完整榜单
            </a>
          </div>

          <div className="mt-4 rounded-lg border border-background-200 bg-secondary-50 p-4">
            <div className="flex items-center gap-2">
              <i className="ri-shield-check-line h-5 w-5 text-secondary-700" style={{ lineHeight: 1 }} />
              <h3 className="text-sm font-semibold text-foreground-900">内容复核说明</h3>
            </div>
            <p className="mt-2 text-xs leading-relaxed text-foreground-600">
              每条瓜料都会标注来源时间与已核对材料数量，涉及尚未证实的内容一律以「待补齐」标记，不做结论性表述。
            </p>
          </div>
        </aside>
      </div>
    </section>
  );
}